import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import { Button } from './shared/SharedComponents';

interface StaleBannerProps {
  publicId: string;
  stalenessReason: string;
  deltaMonthlySavings: number;
}

export const StaleBanner: React.FC<StaleBannerProps> = ({
  publicId,
  stalenessReason,
  deltaMonthlySavings,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  // Format the reason for human reading
  let reasonText = 'Vendor pricing updates have been detected.';
  if (stalenessReason === 'savings_delta_significant') {
    const direction = deltaMonthlySavings >= 0 ? 'increased' : 'decreased';
    const amount = Math.abs(deltaMonthlySavings).toFixed(0);
    reasonText = `Vendor price shifts have ${direction} potential savings by $${amount}/month.`;
  } else if (stalenessReason === 'recommendation_added') {
    reasonText = 'New optimization opportunities have become available due to lower pricing tiers.';
  } else if (stalenessReason === 'recommendation_removed') {
    reasonText = 'Some recommendations are no longer viable due to vendor tier removals.';
  } else if (stalenessReason.startsWith('recommendation_')) {
    reasonText = 'Vendor plans or pricing structures have changed.';
  }

  return (
    <div 
      className="relative mb-12 p-6 md:p-8 rounded-[2rem] border border-amber-500/20 bg-amber-500/5 backdrop-blur-md overflow-hidden animate-in slide-in-from-top duration-700"
      role="status"
    >
      {/* Background radial highlight */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-500/10 text-amber-600 rounded-2xl shrink-0">
            <AlertTriangle size={24} />
          </div>
          <div className="space-y-1">
            <h3 className="font-serif italic text-lg text-amber-900 dark:text-amber-300">
              Pricing Updates Detected
            </h3>
            <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
              {reasonText} This strategic report is now out-of-date. You can compare the differences or re-run the audit.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-end md:self-center shrink-0">
          <Link to={`/re-audit/${publicId}`}>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-amber-500/20 hover:border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/5 bg-transparent shadow-none"
            >
              Compare Audit Diff <ArrowRight size={14} className="ml-1" />
            </Button>
          </Link>
          <button 
            onClick={() => setIsVisible(false)}
            className="p-2 text-muted-foreground hover:text-foreground rounded-full hover:bg-foreground/5 transition-colors"
            aria-label="Dismiss banner"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
